'use client';

import { useState } from 'react';

import { cn } from '@/lib/utils';

import { Toggle } from '@/components/atoms/Toggle';
import {
  BentoPricingCard,
  type BentoPricingCardProps,
} from '@/components/bento/BentoPricingCard';

import BentoBanner, { type BentoBannerProps } from '@/blocks/BentoBanner';

export type BentoPricingCardsTab = {
  label: string;
  cards: BentoPricingCardProps[];
};

export type BentoPricingCardsSectionProps = {
  tabs: BentoPricingCardsTab[];
  banner?: BentoBannerProps;
};

export const BentoPricingCardsSection = ({
  tabs,
  banner,
}: BentoPricingCardsSectionProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  if (!tabs.length) return null;

  const safeIndex =
    selectedIndex >= 0 && selectedIndex < tabs.length ? selectedIndex : 0;
  const showToggle = tabs.length > 1;

  return (
    <div className='flex w-full flex-col gap-6 sm:gap-10'>
      {showToggle && (
        <Toggle
          options={tabs.map((tab) => tab.label)}
          selectedIndex={safeIndex}
          onValueChange={setSelectedIndex}
          size='s'
          classNames={{
            container: 'w-fit',
            root: 'w-fit rounded border-gray-dark-700 bg-gray-dark-900 p-0.5 hover:bg-gray-dark-900',
            toggleItem: {
              button:
                'w-auto font-mono font-normal text-gray-dark-400 md:font-serif md:font-medium',
              selectedButton: 'font-medium text-gray-dark-950',
              inner:
                'w-auto rounded-sm hover:bg-transparent md:hover:bg-transparent px-3! py-1.5! text-body-s-medium! sm:px-3! md:px-6! md:py-2! md:text-button-text-m!',
              selectedPill: 'rounded-sm! bg-gray-dark-200',
            },
          }}
        />
      )}

      {tabs.map((tab, index) => {
        const isActive = index === safeIndex;

        return (
          <ul
            key={tab.label}
            className={cn(
              'grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4',
              !isActive && 'hidden'
            )}
          >
            {tab.cards.map((card, cardIndex) => (
              <li
                key={`${tab.label}-${card.title}-${cardIndex}`}
                className='min-h-0'
              >
                <BentoPricingCard {...card} className='h-full w-full' />
              </li>
            ))}
          </ul>
        );
      })}

      {banner && <BentoBanner {...banner} />}
    </div>
  );
};
